/** The agentic review of an economic concentration (features/agentic). */
export default {
  title: "Agentic Review",
  subtitle: "Economic concentration",
  entry: "Agentic review",
  entryHint: "Open this file in the full-screen agentic review: a team of agents reads, tests and drafts in parallel, and the lead agent asks you what only you decide.",
  exit: "Leave the agentic review",
  play: "Resume",
  pause: "Pause",
  speed: "Speed",
  language: "Switch language",
  lightTheme: "Light version",
  darkTheme: "Dark version",
  close: "Close",
  maximize: "Maximise",
  restore: "Restore",
  chat: { title: "Conversation", you: "You", ask: "Ask {{name}}…" },
  tabs: { conversation: "Conversation", memory: "Memory", sources: "Sources" },
  memory: {
    kicker: "Institutional memory",
    intro: "The Department's practice – method and reasoning, never another file's data – and what this review recorded. A practice is never applied on its own: it is put to the officer as a question, and recorded as the officer's only once answered.",
    recalled: "Recalled",
    remembered: "Remembered",
    recalledFrom: "Recalled from the Department's memory",
    rememberedFor: "Remembered for later files",
    learnedHere: "Learned in this review",
    applied: "Applied from memory",
    forget: "Remove from memory",
    forgotten: "Removed: later files will not offer it",
    kept: "Kept in the Department's memory · audited",
    recalledCount_one: "{{count}} recalled",
    recalledCount_other: "{{count}} recalled",
    learnedCount_one: "{{count}} learned",
    learnedCount_other: "{{count}} learned",
    emptyRecalled: "Nothing recalled yet: the Department's practice is read as the review starts.",
    emptyLearned: "Nothing learned yet: the practice you set and the decisions you take are kept here for later files.",
    kinds: { case: "Earlier file", transaction: "Recorded deal", commitment: "Commitment in force", lesson: "Lesson of an evaluated decision", practice: "Department practice", fact: "Fact of this file", decision: "Decision taken on this file" },
  },
  sources: {
    kicker: "Sources and integrations",
    intro: "Every system the agents reached in this review – the Ministry's own, government registers, licensed feeds and the open web – with the last thing asked of each.",
    reached: "Reached in this review",
    connected: "Connected to the platform",
    calls_one: "{{count}} call",
    calls_other: "{{count}} calls",
    failed_one: "{{count}} failed",
    failed_other: "{{count}} failed",
    agents_one: "{{count}} agent",
    agents_other: "{{count}} agents",
    none: "No source reached yet.",
    live: "Live",
    calls24h_one: "One call in 24 h",
    calls24h_other: "{{count}} calls in 24 h",
    strip: "Sources",
    all: "All sources",
    health: { Healthy: "Healthy", Degraded: "Degraded", Down: "Down", Unknown: "Not checked" },
    unavailable: "The integration register is not available to this account.",
  },
  caseFile: "Case file",
  caseFileHint: "Switch to the conventional case file, the system of record, to check any finding and confirm or override it",
  igniting: "Assembling the agent team…",
  log: "Event stream",
  logKinds: { think: "thinks", tool: "tool", result: "result", find: "finding", ask: "question", done: "done", lead: "lead", spawn: "joins", error: "error" },
  status: { queued: "Queued", thinking: "Thinking", tool: "Using tools", writing: "Writing", waiting: "Needs you", done: "Done", flagged: "Flagged", failed: "Stopped" },
  card: { spawning: "Spinning up…" },
  squad: {
    documents: "Document Intelligence",
    documentsRole_one: "{{count}} reader, one per filed document",
    documentsRole_other: "{{count}} readers in parallel, one per filed document",
  },
  lead: {
    kicker: "Orchestrator",
    name: "Case Lead",
    active_one: "{{count}} agent working",
    active_other: "{{count}} agents working",
    waiting_one: "{{count}} question for you",
    waiting_other: "{{count}} questions for you",
    status: { idle: "Idle", reading: "Reading the file", planning: "Planning", dispatching: "Dispatching", listening: "Coordinating", asking: "Waiting for you", concluding: "Concluding", done: "Done" },
    plan: "Plan · {{phase}}",
    wave: "Wave {{n}}",
    gate: "Your decision · {{phase}}",
    question: "{{agent}} asks",
    carrying: "Carrying out your decision…",
    thinking: "Thinking…",
    ask: "Ask the Case Lead about this file…",
    send: "Send",
    askHint: "Answered by the platform's case assistant, with its tools and the file's documents.",
    pending: "Waiting for you",
  },
  rail: { label: "Procedure", intake: "Notification" },
  stats: { agents: "Agents working / on the canvas", tools: "Tool calls", sources: "Sources read", tokens: "Text read and written, in tokens (estimate)" },
  swarm: {
    agents_one: "{{count}} agent",
    agents_other: "{{count}} agents",
    live: "{{count}} working",
    waiting: "{{count}} need you",
    done: "{{count}} done",
    flagged: "{{count}} flagged",
    backToCurrent: "Back to the current wave",
    assembling: "The lead is assembling this wave…",
  },
  focus: {
    trace: "Trace",
    findings: "Findings",
    tools_one: "{{count}} tool call",
    tools_other: "{{count}} tool calls",
    sources_one: "{{count}} source",
    sources_other: "{{count}} sources",
  },
  intake: {
    kicker: "Economic concentration · notification received",
    headline1: "One notification.",
    headline2: "A whole team of agents.",
    lede: "The company's notification, as filed through the portal – the form and every document. Send in the agents: they read, verify, measure the market and draft in parallel, following the procedure stage by stage; the lead agent asks you what only you can decide.",
    new: "Filed outside the portal",
    newHint: "Register a company's paper or e-mail filing",
    other: "Another file…",
    files: "Filing pack",
    filedPack: "Filed by the company",
    dropLate: "Add a document the company sent later",
    drop: "Drop the filing pack here",
    dropActive: "Release to add to the file",
    dropHint: "Agreements, audited statements, market reports, ownership charts, powers of attorney – PDF, Word, Excel or images",
    uploading: "Uploading and extracting…",
    article10: "Article 10 checklist",
    team: "The agent team for this file",
    teamHint_one: "{{count}} agent across the procedure, working in parallel within each wave",
    teamHint_other: "{{count}} agents across the procedure, working in parallel within each wave",
    begin: "Begin with",
    live: "Also run the platform's model agents",
    liveHint: "The Review Report Writer and the Decision Brief Writer draft on the AI runtime; their drafts arrive as proposals for your approval.",
    liveOffline: "The AI runtime is offline: the agents work with the platform's rules, data and documents.",
    analyse: "Analyse with agents",
    needs: "A title, both parties and a relevant market are needed to start.",
    reading: "Reading",
    remove: "Remove",
    notOnForm: "Not on the form",
    form: "Notification",
    asFiled: "As filed",
    transaction: "Transaction",
    title: "Title",
    type: "Type",
    consideration: "Consideration",
    considerationAed: "Consideration (AED)",
    signing: "Signed",
    closing: "Expected completion",
    description: "Description",
    parties: "Parties",
    control: "Control",
    stake: "Stake acquired (%)",
    preStake: "Held before (%)",
    controlType: "Control",
    markets: "Turnover and relevant markets",
    turnover: "Combined UAE turnover",
    turnoverAed: "Combined UAE turnover (AED)",
    fiscalYear: "Fiscal year",
    newForm: "New notification",
    acquirer: "Acquirer",
    target: "Target",
    licence: "Licence",
    emirate: "Emirate",
    product: "Product market",
    geography: "Geographic market",
    marketSizeAed: "Market size (AED)",
    acquirerShare: "Acquirer share (%)",
    targetShare: "Target share (%)",
    competitors: "Competitors",
    competitor: "Competitor",
    addCompetitor: "Add a competitor",
  },
  /** Counted words of the agents' text (mission/text.ts count): the plural forms of each catalogue. */
  count: {
    days_one: "{{count}} day", days_other: "{{count}} days",
    workingDays_one: "{{count}} working day", workingDays_other: "{{count}} working days",
    documents_one: "{{count}} document", documents_other: "{{count}} documents",
    parties_one: "{{count}} party", parties_other: "{{count}} parties",
    markets_one: "{{count}} relevant market", markets_other: "{{count}} relevant markets",
    agents_one: "{{count}} agent", agents_other: "{{count}} agents",
    toolCalls_one: "{{count}} tool call", toolCalls_other: "{{count}} tool calls",
    sources_one: "{{count}} source", sources_other: "{{count}} sources",
    decisions_one: "{{count}} decision", decisions_other: "{{count}} decisions",
    practices_one: "{{count}} practice", practices_other: "{{count}} practices",
    memories_one: "{{count}} memory", memories_other: "{{count}} memories",
    gaps_one: "{{count}} missing item", gaps_other: "{{count}} missing items",
    letters_one: "{{count}} letter", letters_other: "{{count}} letters",
    redactions_one: "{{count}} redaction", redactions_other: "{{count}} redactions",
    sections_one: "{{count}} section", sections_other: "{{count}} sections",
    figures_one: "{{count}} figure", figures_other: "{{count}} figures",
    firms_one: "{{count}} undertaking", firms_other: "{{count}} undertakings",
    years_one: "{{count}} year", years_other: "{{count}} years",
    passages_one: "{{count}} passage", passages_other: "{{count}} passages",
    instruments_one: "{{count}} instrument", instruments_other: "{{count}} instruments",
    items_one: "{{count}} item", items_other: "{{count}} items",
    pages_one: "{{count}} page", pages_other: "{{count}} pages",
    entities_one: "{{count}} entity", entities_other: "{{count}} entities",
    links_one: "{{count}} link", links_other: "{{count}} links",
    nodes_one: "{{count}} node", nodes_other: "{{count}} nodes",
    queries_one: "{{count}} query", queries_other: "{{count}} queries",
    discrepancies_one: "{{count}} discrepancy", discrepancies_other: "{{count}} discrepancies",
    views_one: "{{count}} view", views_other: "{{count}} views",
    objections_one: "{{count}} objection", objections_other: "{{count}} objections",
    authorities_one: "{{count}} authority", authorities_other: "{{count}} authorities",
    regulators_one: "{{count}} regulator", regulators_other: "{{count}} regulators",
    facts_one: "{{count}} fact", facts_other: "{{count}} facts",
    characters_one: "{{count}} character", characters_other: "{{count}} characters",
    checklistItems_one: "{{count}} checklist item", checklistItems_other: "{{count}} checklist items",
    acquirers_one: "{{count}} acquirer", acquirers_other: "{{count}} acquirers",
    points_one: "{{count}} point", points_other: "{{count}} points",
    licences_one: "{{count}} licence", licences_other: "{{count}} licences",
    records_one: "{{count}} record", records_other: "{{count}} records",
    elements_one: "{{count}} element", elements_other: "{{count}} elements",
    checks_one: "{{count}} check", checks_other: "{{count}} checks",
  },
  types: { Acquisition: "Acquisition", Merger: "Merger", JointVenture: "Joint venture", AssetTransfer: "Asset transfer", ControlChange: "Change of control" },
  roles: { AcquiringParty: "Acquirer", TargetParty: "Target", Representative: "Representative", Seller: "Seller", Other: "Party" },
  control: { Unknown: "Not stated", None: "No control", Sole: "Sole control", Joint: "Joint control" },
};
